<template>
  <div class="question-selector">
    <!-- Botón para abrir el selector -->
    <v-card
      variant="outlined"
      :class="['selector-trigger', { 'selected': modelValue, 'disabled': !canSearch }]"
      @click="handleOpenDialog"
      :hover="canSearch"
    >
      <v-card-text class="d-flex align-center">
        <v-icon
          :color="modelValue ? 'success' : !canSearch ? 'grey-lighten-1' : 'grey'"
          class="mr-3"
          size="large"
        >
          {{ modelValue ? 'mdi-check-circle' : 'mdi-plus-circle-outline' }}
        </v-icon>
        <div class="flex-grow-1">
          <div v-if="modelValue && selectedQuestion" class="selected-question">
            <div class="text-subtitle-2 text-primary font-weight-bold mb-1">
              Pregunta seleccionada
            </div>
            <div class="text-body-2 selected-statement">
              <MathRenderer :content="truncateText(selectedQuestion.statement, 80)" />
            </div>
            <v-chip size="small" color="success" variant="tonal" class="mt-2">
              ID: {{ modelValue }}
            </v-chip>
          </div>
          <div v-else class="no-selection">
            <div class="text-subtitle-1 font-weight-medium" :class="{ 'text-disabled': !canSearch }">
              Seleccionar pregunta específica (opcional)
            </div>
            <div class="text-caption" :class="canSearch ? 'text-medium-emphasis' : 'text-disabled'">
              {{ canSearch ? 'Haz clic para buscar y seleccionar una pregunta' : 'Primero selecciona un grupo y una asignatura' }}
            </div>
          </div>
        </div>
        <v-btn
          v-if="modelValue"
          icon
          variant="text"
          size="small"
          color="error"
          @click.stop="clearSelection"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-icon v-else :color="canSearch ? 'primary' : 'grey-lighten-1'">
          {{ canSearch ? 'mdi-magnify' : 'mdi-lock' }}
        </v-icon>
      </v-card-text>
    </v-card>

    <!-- Tooltip informativo cuando está deshabilitado -->
    <v-tooltip v-if="!canSearch && !modelValue" activator="parent" location="bottom">
      <span>Selecciona primero un grupo y una asignatura para habilitar esta opción</span>
    </v-tooltip>

    <!-- Dialog de búsqueda y selección -->
    <v-dialog v-model="dialog" max-width="900px" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center bg-primary text-white pa-4">
          <v-icon class="mr-2">mdi-text-search</v-icon>
          Buscar y Seleccionar Pregunta
          <v-spacer />
          <v-btn icon variant="text" @click="closeDialog" color="white">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Filtros -->
          <v-row class="mb-4">
            <v-col cols="12">
              <v-text-field
                v-model="searchQuery"
                label="Buscar por enunciado"
                prepend-inner-icon="mdi-magnify"
                variant="outlined"
                clearable
                hide-details
                @input="debouncedSearch"
                placeholder="Escribe para buscar..."
              >
                <template v-slot:append-inner>
                  <v-progress-circular
                    v-if="searching"
                    indeterminate
                    size="20"
                    width="2"
                    color="primary"
                  />
                </template>
              </v-text-field>
            </v-col>
          </v-row>

          <!-- Lista de preguntas -->
          <div v-if="loading && !searching" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" size="64" />
            <p class="mt-4 text-medium-emphasis">Cargando preguntas...</p>
          </div>

          <div v-else-if="questions.length === 0" class="text-center py-8">
            <v-icon size="64" color="grey">mdi-file-question-outline</v-icon>
            <p class="text-h6 mt-4">No se encontraron preguntas</p>
            <p class="text-caption text-medium-emphasis">
              {{ searchQuery ? 'Intenta con otros términos de búsqueda' : 'Ajusta los filtros o crea una nueva pregunta' }}
            </p>
          </div>

          <v-list v-else class="question-list">
            <v-list-item
              v-for="question in questions"
              :key="question.id"
              :value="question.id"
              @click="selectQuestion(question)"
              :class="{ 'selected-item': tempSelected === question.id }"
              class="question-item mb-2"
              rounded
            >
              <template v-slot:prepend>
                <v-avatar :color="tempSelected === question.id ? 'success' : 'grey-lighten-2'">
                  <v-icon :color="tempSelected === question.id ? 'white' : 'grey'">
                    {{ tempSelected === question.id ? 'mdi-check-circle' : 'mdi-help-circle-outline' }}
                  </v-icon>
                </v-avatar>
              </template>

              <v-list-item-title class="text-wrap mb-2 question-list-statement">
                <MathRenderer :content="truncateText(question.statement, 120)" />
              </v-list-item-title>

              <v-list-item-subtitle>
                <v-chip size="x-small" color="primary" variant="tonal" class="mr-1">
                  {{ question.school_subject?.name || 'Sin asignatura' }}
                </v-chip>
                <v-chip size="x-small" color="info" variant="tonal" class="mr-1">
                  Grado {{ question.school_grade?.grade || 'N/A' }}
                </v-chip>
                <v-chip size="x-small" color="success" variant="tonal">
                  ID: {{ question.id }}
                </v-chip>
              </v-list-item-subtitle>

              <template v-slot:append>
                <v-btn
                  icon
                  variant="text"
                  size="small"
                  @click.stop="previewQuestion(question)"
                >
                  <v-icon>mdi-eye</v-icon>
                </v-btn>
              </template>
            </v-list-item>
          </v-list>

          <!-- Paginación -->
          <div v-if="totalPages > 1" class="d-flex justify-center align-center mt-4">
            <v-pagination
              v-model="currentPage"
              :length="totalPages"
              :total-visible="5"
              @update:model-value="loadQuestions"
              rounded="circle"
            />
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-btn @click="closeDialog" variant="text">
            Cancelar
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            variant="elevated"
            @click="confirmSelection"
            :disabled="!tempSelected"
          >
            <v-icon left>mdi-check</v-icon>
            Confirmar selección
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de vista previa usando QuestionDetail -->
    <v-dialog v-model="previewDialog" max-width="1000px" scrollable>
      <v-card v-if="previewQuestionData">
        <v-card-title class="bg-info text-white pa-4 d-flex align-center">
          <v-icon class="mr-2">mdi-eye</v-icon>
          Vista Previa de la Pregunta
          <v-spacer />
          <v-btn icon variant="text" @click="previewDialog = false" color="white">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-0">
          <!-- Usar QuestionDetail con hideActions para no mostrar botones -->
          <QuestionDetail
            :question-id="previewQuestionData.id"
            :hide-actions="true"
          />
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn @click="previewDialog = false" variant="text">
            Cerrar
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            @click="selectFromPreview"
          >
            <v-icon left>mdi-check</v-icon>
            Seleccionar esta pregunta
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import QuestionDetail from '@/components/questions/QuestionDetail.vue'
import MathRenderer from '@/components/common/MathRenderer.vue'

const props = defineProps({
  modelValue: {
    type: [Number, null],
    default: null
  },
  schoolGradeId: {
    type: [Number, String],
    required: true
  },
  schoolSubjectId: {
    type: [Number, String],
    required: true
  }
})

const emit = defineEmits(['update:modelValue', 'question-selected'])

const store = useStore()

// State
const dialog = ref(false)
const previewDialog = ref(false)
const searchQuery = ref('')
const currentPage = ref(1)
const tempSelected = ref(null)
const selectedQuestion = ref(null)
const previewQuestionData = ref(null)
const searching = ref(false)
const questions = ref([])
const totalPages = ref(1)
const loading = ref(false)

let searchTimeout = null

// Computed
const canSearch = computed(() => {
  return props.schoolGradeId && props.schoolSubjectId
})

// Methods
const truncateText = (text, maxLength) => {
  if (!text) return ''
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
}

const handleOpenDialog = () => {
  if (!canSearch.value) {
    // Mostrar mensaje de error si intenta abrir sin los campos requeridos
    console.warn('Debe seleccionar un grupo y una asignatura primero')
    return
  }
  openDialog()
}

const openDialog = () => {
  dialog.value = true
  tempSelected.value = props.modelValue
  loadQuestions()
}

const closeDialog = () => {
  dialog.value = false
  searchQuery.value = ''
  currentPage.value = 1
}

const clearSelection = () => {
  emit('update:modelValue', null)
  emit('question-selected', null)
  selectedQuestion.value = null
  tempSelected.value = null
}

const selectQuestion = (question) => {
  tempSelected.value = question.id
}

const confirmSelection = () => {
  const question = questions.value.find(q => q.id === tempSelected.value)
  if (question) {
    selectedQuestion.value = question
    emit('update:modelValue', question.id)
    emit('question-selected', question)
  }
  closeDialog()
}

const previewQuestion = (question) => {
  previewQuestionData.value = question
  previewDialog.value = true
}

const selectFromPreview = () => {
  if (previewQuestionData.value) {
    tempSelected.value = previewQuestionData.value.id
    confirmSelection()
    previewDialog.value = false
  }
}

const debouncedSearch = () => {
  searching.value = true
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    currentPage.value = 1
    loadQuestions()
  }, 500)
}

const loadQuestions = async () => {
  if (!canSearch.value) return

  loading.value = true

  try {
    const params = {
      school_grade_id: props.schoolGradeId,
      school_subject_id: props.schoolSubjectId,
      page: currentPage.value,
      limit: 10,
      search: searchQuery.value || undefined
    }

    const result = await store.dispatch('questions/fetchQuestions', params)

    if (result.success) {
      questions.value = result.data.questions || []
      totalPages.value = result.data.totalPages || 1
    }
  } catch (error) {
    console.error('Error loading questions:', error)
  } finally {
    loading.value = false
    searching.value = false
  }
}

// Load selected question details if modelValue is provided
watch(() => props.modelValue, async (newValue) => {
  if (newValue && !selectedQuestion.value) {
    try {
      const result = await store.dispatch('questions/fetchQuestionById', newValue)
      if (result.success) {
        selectedQuestion.value = result.data
      }
    } catch (error) {
      console.error('Error loading selected question:', error)
    }
  }
}, { immediate: true })

// Watch for changes in schoolGradeId or schoolSubjectId
// Clear selection if these change
watch([() => props.schoolGradeId, () => props.schoolSubjectId], ([newGrade, newSubject], [oldGrade, oldSubject]) => {
  // Si cambian los filtros y hay una selección, limpiarla
  if ((newGrade !== oldGrade || newSubject !== oldSubject) && props.modelValue) {
    console.log('Filtros cambiaron, limpiando selección de pregunta')
    clearSelection()
  }
})
</script>

<style scoped>
.question-selector {
  width: 100%;
}

.selector-trigger {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

/* Estilos para enunciados con LaTeX */
.selected-statement {
  overflow: hidden;
  max-height: 3em;
  line-height: 1.5em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.selected-statement :deep(.math-renderer) {
  display: inline;
}

.question-list-statement :deep(.math-renderer) {
  display: inline;
}

.selector-trigger:hover:not(.disabled) {
  border-color: rgb(var(--v-theme-primary));
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.selector-trigger.selected {
  background: rgba(var(--v-theme-success), 0.05);
  border-color: rgb(var(--v-theme-success));
}

.selector-trigger.disabled {
  cursor: not-allowed;
  opacity: 0.6;
  background: rgba(0, 0, 0, 0.02);
}

.selector-trigger.disabled * {
  pointer-events: none;
}

.selected-question {
  animation: fadeIn 0.3s ease;
}

.no-selection {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.question-list {
  max-height: 500px;
  overflow-y: auto;
}

.question-item {
  border: 2px solid transparent;
  transition: all 0.2s ease;
  cursor: pointer;
}

.question-item:hover {
  background: rgba(var(--v-theme-primary), 0.05);
  border-color: rgb(var(--v-theme-primary));
}

.question-item.selected-item {
  background: rgba(var(--v-theme-success), 0.1);
  border-color: rgb(var(--v-theme-success));
}

/* Scrollbar personalizado */
.question-list::-webkit-scrollbar {
  width: 8px;
}

.question-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.question-list::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.question-list::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
